import { groupBy } from 'lodash-es';
import type { ConnectedTypes } from '../../../make-connected';
import type { Types } from '../../../types';
import { getVertexId } from '../../../../core/util';
import { makeChainable } from '../../../../common/make-chainable';
import type { AvailableLocation } from '../../../../core/interfaces/vertices/util/edges';


type LocationReference = AvailableLocation.Util.Value<ConnectedTypes.location, { groupIndex?: number; rank?: number; }>;

// Normalize locked-time coalesced edges into the location reference shape used elsewhere.
function toLocationReferences (coalesced: ConnectedTypes.lockedTime['coalesced']): LocationReference[] | undefined {
  return coalesced
    ?.map((x, i): LocationReference | null => x.toModel == 'locations'
      ?  { locations: [x.to as ConnectedTypes.location], groupIndex: i, rank: 0 }
      : null
    )
    .filter(x => x != null);
}

function isCoalescedReferenceList (
  references: LocationReference[] | ConnectedTypes.lockedTime['coalesced'] | null | undefined
): references is NonNullable<ConnectedTypes.lockedTime['coalesced']> {
  return !!references?.[0] && 'toModel' in references[0];
}

export function parseLocationReferences (
  references: LocationReference[] | null | undefined,
  options:    Types.parsedToOptions
): Types.Shared.DependencyReference[][] | undefined;
export function parseLocationReferences (
  references: ConnectedTypes.lockedTime['coalesced'],
  options:    Types.parsedToOptions
): Types.Shared.DependencyReference[][] | undefined;

export function parseLocationReferences (
  references: LocationReference[] | ConnectedTypes.lockedTime['coalesced'] | null | undefined,
  options:    Types.parsedToOptions
) {
  // Support both event-style location references and locked-time coalesced edges.
  const normalizedReferences: LocationReference[] | null | undefined = isCoalescedReferenceList(references)
    ? toLocationReferences(references)
    : references;

  if (!normalizedReferences) return;

  return makeChainable(normalizedReferences)
    .chain(
      x => x
        .map(({ locations, groupIndex, rank }) => ({
          dependency: getVertexId(locations[0], options),
          rank:       rank,
          groupIndex: groupIndex
        }))
        .filter(x => {
          // remove all references to excluded locations based on partial schedule options
          const includedLocations = options.partialScheduleOptions?.includedLocations;
          if (!includedLocations) return true;
          return includedLocations.has(x.dependency);
        }),
      x => groupBy(x, x => x.groupIndex),
      x => Object.values(x)
        .map(xs => xs.map(x => ({
          dependency: x.dependency,
          ...x.rank != null && { rank: x.rank },
        }) as Types.Shared.DependencyReference))
    )
    .value;
}

// export function parseSelectedLocations (event: ConnectedTypes.event, options: Types.parsedToOptions) {

//   const locationsRef = event.locations ?? event.course?.locations;
//   const dependencies = parseLocationReferences(locationsRef, options);
//   const inLocations  = event.inLocations ?? [];

//   // simply use inLocations if they match the number of dependencies
//   if (inLocations.length === dependencies?.length) {
//     return inLocations.map(x =>  x ? getVertexId(x, options) : null);
//   }

//   // otherwise, fill inLocations with nulls to match the number of dependencies
//   return inLocations
//     .map(x =>  x ? getVertexId(x, options) : null)
//     .concat(Array
//       .from({ length: Math.max((dependencies?.length ?? 0) - inLocations.length, 0) })
//       .map(() => null as string | null)
//     );
// }